import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Trans, t } from '@lingui/macro';
import { Alert, Dialog } from '@economic/taco';
import { useCurrentAgreement, useLocalStorage } from '@economic/react-app';

import { PartialInvoiceDistributionInfoDto, PartialInvoiceDistributionType, PartialInvoiceDto } from '~/types/internal-api';
import InfoDialog from '~/app/shared/components/modals/InfoDialog';
import CreatePartialInvoiceForm from './AmountBasedPartialInvoicing/CreatePartialInvoiceForm';
import LineBasedCreatePartialInvoiceForm from './ProductLineBasedPartialInvoicing/LineBasedCreatePartialInvoiceForm';
import { SalesDocument } from '../../types';
import DistributionTypeSelect from './DistributionTypeSelect';
import ExistingPartialInvoicesInfo from './ExistingPartialInvoicesInfo';
import ConvertToInvoice from './ConvertToInvoice';

interface Props {
    order: SalesDocument;
    partialInvoices: PartialInvoiceDto[];
    close: () => void;
    partialInvoiceDistributionTypeInfo: PartialInvoiceDistributionInfoDto;
}

export default function CreatePartialInvoice({ order, partialInvoices, close, partialInvoiceDistributionTypeInfo }: Props) {
    const location = useLocation();
    const agreement = useCurrentAgreement();

    const [showConvertToInvoiceConfirmation, setShowConvertToInvoiceConfirmation] = useState<boolean>(false);
    const [distributionType, setDistributionType] = useState<PartialInvoiceDistributionType>(
        partialInvoiceDistributionTypeInfo?.distributionType === undefined
            ? PartialInvoiceDistributionType.AmountBased
            : partialInvoiceDistributionTypeInfo.distributionType
    );
    const [showInfoAlert, setShowInfoAlert] = useLocalStorage<boolean>(`showPartialInvoicingInfoAlert`, true);

    const salesDocumentId = location.state?.draftId;

    if (order.amount <= 0) {
        return (
            <InfoDialog
                onClose={close}
                label={t`Cannot create a partial invoice`}
                message={t`Order amount must be more than 0 to create a partial invoice.`}
            />
        );
    }
    if (showConvertToInvoiceConfirmation) {
        return (
            <ConvertToInvoice
                distributionType={distributionType}
                cancel={() => setShowConvertToInvoiceConfirmation(false)}
                orderId={order.salesDocumentId}
            />
        );
    }

    return (
        <>
            <Dialog.Title className="mb-6">
                <Trans>Partial invoice</Trans>
            </Dialog.Title>
            {partialInvoices.length > 0 ? (
                <ExistingPartialInvoicesInfo openedInCreatePartialInvoiceDialog order={order} partialInvoices={partialInvoices} />
            ) : (
                showInfoAlert &&
                agreement.features.partialInvoicingLineBased && (
                    <Alert
                        state="information"
                        title={t`Partial invoices on product lines`}
                        onClose={() => setShowInfoAlert(false)}>
                        <Trans>
                            You can now choose whether you want to create a partial invoice based on your product lines or based
                            on an amount
                        </Trans>
                    </Alert>
                )
            )}

            {agreement.features.partialInvoicingLineBased && (
                <div className="flex items-end mb-4">
                    <DistributionTypeSelect
                        disabled={partialInvoiceDistributionTypeInfo?.distributionType !== undefined}
                        showInfoAlert={showInfoAlert}
                        defaultValue={distributionType}
                        onChange={value => setDistributionType(value)}
                    />
                </div>
            )}

            {distributionType === PartialInvoiceDistributionType.AmountBased && (
                <CreatePartialInvoiceForm
                    close={close}
                    orderId={salesDocumentId}
                    orderAmount={order.amount}
                    orderRemainingAmount={order.amount - partialInvoices.reduce((sum, el) => sum + el.amount, 0)}
                    handleShowConvertToInvoiceConfirmation={() => setShowConvertToInvoiceConfirmation(true)}
                    hasDraftPartialInvoices={partialInvoices.filter(el => !el.booked).length > 0}
                    partialInvoicesCount={partialInvoices.length}
                />
            )}

            {distributionType === PartialInvoiceDistributionType.ProductLineBased && (
                <LineBasedCreatePartialInvoiceForm
                    salesDocumentId={salesDocumentId}
                    close={close}
                    handleShowConvertToInvoiceConfirmation={() => setShowConvertToInvoiceConfirmation(true)}
                    hasDraftPartialInvoices={partialInvoices.filter(el => !el.booked).length > 0}
                    partialInvoicesCount={partialInvoices.length}
                />
            )}
        </>
    );
}
